import dynamic from 'next/dynamic';
import { useCallback, useEffect } from 'react';

import { FunnelEmploymentTypeNudge, FunnelLocationQuestion, useMultipleVariantData } from '@pflegia/feature-flags';
import { EmploymentType } from '@pflegia/types/api/candidate/employment-type';
import { QualificationRecognition } from '@pflegia/types/api/candidate/recognition';
import { FunnelQuestion } from '@pflegia/types/api/funnel/questions';
import { RecognitionStatus } from '@pflegia/types/common/candidate/recognition-status';
import { CandidateActions } from '@pflegia/types/common/enums/post-back/candidate-actions.enum';
import { LocationObject } from '@pflegia/types/common/location/location-object';
import { type useFunnel } from '@pflegia/ui/funnel/hooks/use-funnel';
import { useFunnelState } from '@pflegia/ui/funnel/hooks/use-funnel-state';
import { EmploymentTypeAnswer } from '@pflegia/ui/pflegia/funnel/types/employment-type-answer';
import Spinner from '@pflegia/ui/shared/spinner';

import { Qualification } from '@shared/application/interfaces';
import useRegisterMethod from '@shared/hooks/use-register-method';
import { useTrackUtm } from '@shared/hooks/use-track-utm';

import QuestionHeader from './QuestionHeader';

const loading = () => <Spinner />;
const NameAndEmailQuestion = dynamic(() => import('./NameAndEmailQuestion'), { loading });
const CityAreasQuestion = dynamic(() => import('../city-areas-question/CityAreasQuestion.component'), { loading });
const CanStartOnQuestion = dynamic(() => import('./CanStartOnQuestion'), { loading });
const HighlightJobTitle = dynamic(() => import('./HighlightJobTitle'), { loading });
const CvFilesQuestion = dynamic(() => import('../multiposting/CvFilesQuestion'), { loading });
const DepartmentQuestion = dynamic(() => import('./DepartmentQuestion'), { loading });
const EmployeeTypeQuestion = dynamic(() => import('./EmployeeTypeQuestion'), { loading });
const EmploymentTypeQuestion = dynamic(() => import('./EmploymentTypeQuestion'), { loading });
const JobTitleQuestion = dynamic(() => import('./JobTitleQuestion'), { loading });
const JobQualificationStep = dynamic(() => import('../multiposting/JobQualificationStep'), { loading });
const LocationQuestion = dynamic(() => import('../location-question/LocationQuestion.component'), { loading });
const PersonalDataStep = dynamic(() => import('../multiposting/PersonalDataStep'), { loading });
const PhoneNumberQuestion = dynamic(() => import('../multiposting/PhoneNumberForm'), { loading });
const PotentialJobsPreview = dynamic(() => import('../potential-jobs/PotentialJobsPreview.component'), { loading });
const QualificationQuestionWithQuery = dynamic(
  () => import('../qualification-question/QualificationQuestion.component').then(mod => mod.QualificationQuestionWithQuery),
  { loading },
);
const SyncSingleChoiceQuestion = dynamic(() => import('./SyncSingleChoiceQuestion'), { loading });
const QualificationRecognitionQuestion = dynamic(() => import('./QualificationRecognitionQuestion'), { loading });

export type ComponentDeciderProps = Pick<ReturnType<typeof useFunnel>, 'onAnswer' | 'setValue' | 'value' | 'setDisableContinue'> & {
  question: FunnelQuestion;
  questionKeys: FunnelQuestion[];
  locationVariant?: FunnelLocationQuestion.VariantType;
};

/**
 * Renders the component based on the `question` prop.
 */
const LocalFunnelDecider: React.FC<ComponentDeciderProps> = ({
  onAnswer,
  question,
  questionKeys,
  setValue,
  value,
  setDisableContinue,
  locationVariant,
}) => {
  const { trackUtm } = useTrackUtm();
  const { isMultipostingFunnel, isTransparentFunnel } = useRegisterMethod();

  useEffect(() => {
    if (
      question === FunnelQuestion.NursingAreaWorking ||
      ((isMultipostingFunnel || isTransparentFunnel) && question === FunnelQuestion.CanStartOn)
    ) {
      trackUtm(CandidateActions.StartedRegistration);
    }
  }, [isMultipostingFunnel, isTransparentFunnel, question, trackUtm]);

  const handleQualificationRecognitionValue = useCallback(
    (val?: QualificationRecognition) => {
      if (val?.status === RecognitionStatus.InProgress) setValue(val);
      else onAnswer(val);
    },
    [setValue, onAnswer],
  );

  const locations = useFunnelState(state => state[FunnelQuestion.Locations]) as LocationObject[] | undefined;
  const questionHeader = <QuestionHeader question={question} />;
  const { isTest: isEmploymentTypeNudgeEnabled } = useMultipleVariantData<boolean>(FunnelEmploymentTypeNudge.Name);

  switch (question) {
    case FunnelQuestion.AQualification:
    case FunnelQuestion.BQualification:
    case FunnelQuestion.CaregiverQualification:
    case FunnelQuestion.CQualification:
    case FunnelQuestion.DQualification:
    case FunnelQuestion.FurtherQualifications:
    case FunnelQuestion.GermanSpeakingLevel:
    case FunnelQuestion.HaveDriverLicense:
    case FunnelQuestion.NursingAreaWorking:
    case FunnelQuestion.PermanentOrTemporary:
    case FunnelQuestion.PositionDeputy:
    case FunnelQuestion.YearsOfExperience:
    case FunnelQuestion.JobYearsOfExperience:
      return (
        <SyncSingleChoiceQuestion
          data-testid='sync-single-choice-question'
          question={question}
          questionHeader={questionHeader}
          setValue={val => {
            onAnswer(val.toString());
          }}
        />
      );

    case FunnelQuestion.BQualificationRecognition:
    case FunnelQuestion.CQualificationRecognition:
    case FunnelQuestion.DQualificationRecognition:
    case FunnelQuestion.JobQualificationRecognition:
      return (
        <QualificationRecognitionQuestion
          question={question}
          setValue={handleQualificationRecognitionValue}
          setDisableContinue={setDisableContinue}
        />
      );

    case FunnelQuestion.CanStartOn:
      return (
        <CanStartOnQuestion
          questionHeader={questionHeader}
          setValue={val => {
            onAnswer(val.toString());
          }}
        />
      );

    case FunnelQuestion.HighlightJobTitle:
      return <HighlightJobTitle questionHeader={questionHeader} onAnswer={onAnswer} />;

    case FunnelQuestion.CvFiles:
      return <CvFilesQuestion onAnswer={onAnswer} />;

    case FunnelQuestion.Departments:
      return <DepartmentQuestion questionHeader={questionHeader} setValue={setValue} value={(value as string[]) || []} />;

    case FunnelQuestion.EmployeeType:
      return <EmployeeTypeQuestion questionHeader={questionHeader} setValue={setValue} value={(value as string[]) || []} />;

    case FunnelQuestion.EmploymentType:
      return (
        <div className='mb-6'>
          <EmploymentTypeQuestion
            isNudgeEnabled={isEmploymentTypeNudgeEnabled}
            setValue={val => {
              const keepStep =
                val.employmentTypes.includes(EmploymentType.PartTime) ||
                (isEmploymentTypeNudgeEnabled && val.employmentTypes.includes(EmploymentType.Marginal));
              if (keepStep) setValue(val);
              else onAnswer(val);
            }}
            title={questionHeader}
            value={value as EmploymentTypeAnswer}
          />
        </div>
      );

    case FunnelQuestion.JobTitle:
      return <JobTitleQuestion questionHeader={questionHeader} onAnswer={onAnswer} />;

    case FunnelQuestion.JobQualification:
      return <JobQualificationStep onAnswer={onAnswer} />;

    case FunnelQuestion.Locations:
      return (
        <LocationQuestion
          initialLocations={locationVariant === FunnelLocationQuestion.VariantType.AreaStep ? locations : undefined}
          onAnswer={onAnswer}
          showAccuracyNudge={locationVariant === FunnelLocationQuestion.VariantType.Nudge}
        />
      );

    case FunnelQuestion.CityAreas:
      return <CityAreasQuestion locations={locations ?? []} onAnswer={onAnswer} />;

    case FunnelQuestion.PersonalData:
      return <PersonalDataStep />;

    case FunnelQuestion.PhoneNumber:
      return <PhoneNumberQuestion onAnswer={onAnswer} />;

    case FunnelQuestion.Qualifications:
      return <QualificationQuestionWithQuery setValue={setValue} value={(value as Qualification[]) ?? []} />;

    case FunnelQuestion.PotentialJobsPreview:
      // Record a defined answer so the funnel treats the step as answered and doesn't route back to it.
      return <PotentialJobsPreview onNext={() => onAnswer(true)} />;

    case FunnelQuestion.NameAndEmail:
      return <NameAndEmailQuestion questionKeys={questionKeys} />;

    default:
      throw new Error(`Invalid question: ${question}`);
  }
};

export default LocalFunnelDecider;
